import React from "react";
import { IFileUploadState } from "@/components/chatform/chatform";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Note } from "@phosphor-icons/react";
import { isValidFile, isValidImage } from "@/lib/utils";

export interface FileUploadProps {
    fileUploadState: IFileUploadState | undefined;
    setFileUploadState: (value: React.SetStateAction<IFileUploadState | undefined>) => void;
}

export const FileUpload = ({ fileUploadState, setFileUploadState }: FileUploadProps) => {
    const fileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files !== null && files.length > 0) {
            // Sanity check the file size and type. 3.5mb for images, 4.5mb for docs.
            // Use error field to display errors to the user.
            const ext = files[0].name.split(".").pop()?.toLowerCase();
            if (isValidFile(ext || "") && files[0].size > 4500000) {
                setFileUploadState({
                    base64file: undefined,
                    filename: undefined,
                    error: "Your file is too large. Max size for docs is 4.5mb.",
                });
                return;
            } else if (isValidImage(ext || "") && files[0].size > 3700000) {
                setFileUploadState({
                    base64file: undefined,
                    filename: undefined,
                    error: "Your image is too large. Max size for images is 3.7mb.",
                });
                return;
            } else {
                // Read in the file as a base64 string
                const reader = new FileReader();
                reader.onload = (e) => {
                    const result = e.target?.result;
                    if (typeof result === "string") {
                        const fileBytes = result.split(",")[1];
                        // setState of the fileBytes and the file name.
                        setFileUploadState({
                            base64file: fileBytes,
                            filename: files[0].name,
                            error: undefined,
                        });
                    }
                };
                reader.readAsDataURL(files[0]);
            }
        }
    };

    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild={true}>
                    <label htmlFor={"chatFile"}>
                        <Note
                            size={28}
                            className={`mr-2 mt-[3px] cursor-pointer ${fileUploadState === undefined ? "opacity-50" : ""} hover:opacity-100 transition-opacity`}
                        />
                        <input
                            type={"file"}
                            className={"hidden"}
                            id={"chatFile"}
                            data-testid={"chatFile"}
                            // accept={
                            //     ".pdf,.csv,.doc,.docx,.xls,.xlsx,.html,.txt,.md,.jpg,.jpeg,.png,.gif,.webp"
                            // }
                            onChange={fileUpload}
                            onClick={(e) => {
                                e.currentTarget.value = "";
                            }}
                        />
                    </label>
                </TooltipTrigger>
                <TooltipContent
                    className={"text-sm shadow-none bg-zinc-900 text-white rounded-xl"}
                    sideOffset={20}
                >
                    Upload a document or image
                    <div className={"opacity-60 text-xs"}>
                        pdf, csv, doc, docx, xls, xlsx, html, txt, md
                        <br />
                        jpg, jpeg, png, gif, webp
                        <br />
                        Max doc size: 4.5mb
                        <br />
                        Max image size: 3.7mb, 8000px x 8000px
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
